import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import "./ZapierIntegrationPage.css";
import GoogleDrivePage from "./Gdrive";
import UrlImportPage from "./UrlImportPage";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";

const AttachmentMethodsPage = ({ onClose }) => {
  const navigate = useNavigate();

  const [selectedMethod, setSelectedMethod] = useState(null);

  const [showDriveInside, setShowDriveInside] = useState(false);
  const [showUrlInside, setShowUrlInside] = useState(false);

  // ── Zapier state ────────────────────────────────────────
  const workspaceId = "eca5db98-571b-4735-81b4-505676753b64";

  const [integration, setIntegration] = useState(null);
  const [zapLoading, setZapLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [zapError, setZapError] = useState("");
  const [zapierExpanded, setZapierExpanded] = useState(false);

  // ── Manual Upload state ─────────────────────────────────
  const [manualDepartment, setManualDepartment] = useState("HR");
  const [manualDeptOpen, setManualDeptOpen] = useState(false);
  const [manualVerified, setManualVerified] = useState(false);
  const [manualFiles, setManualFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // ── Database form state ─────────────────────────────────
  const [dbForm, setDbForm] = useState({
    type: "PostgreSQL",
    host: "",
    port: "",
    username: "PostgreSQL",
    password: "",
  });

  // ── URL Scraping state ──────────────────────────────────
  const [urlForm, setUrlForm] = useState({
    url: "",
    singlePage: true,
    allSubpages: false,
  });

  // ── Effects ─────────────────────────────────────────────
  useEffect(() => {
    if (selectedMethod === "zapier") {
      fetchIntegration();
   } else {
  setZapierExpanded(false);
  setShowToken(false);
  setZapError("");
  setIntegration(null);
}

    if (selectedMethod !== "manual_upload") {
      setManualDeptOpen(false);
    }

    if (selectedMethod !== "google_drive") {
      setShowDriveInside(false);
    }

    if (selectedMethod !== "url_scraping") {
      setShowUrlInside(false);
    }
  }, [selectedMethod]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".amp-custom-select")) {
        setManualDeptOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ── Zapier logic ────────────────────────────────────────
  const fetchIntegration = async () => {
    try {
      const res = await api.get(
        `/integrations/zapier?workspaceId=${workspaceId}`
      );

      const data = res.data;

      if (data && data.token) {
        setIntegration({
          webhookUrl:
            data.webhookUrl ||
            `${process.env.REACT_APP_API_BASE_URL}/api/integrations/zapier/import`,
          token: data.token,
        });
      } else {
        setIntegration(null);
      }
    } catch (err) {
      console.error("Integration fetch failed:", err);
      setIntegration(null);
    }
  };

  const handleZapierEnable = async () => {
    setZapError("");

    if (integration) {
      setZapierExpanded(true);
      return;
    }

    try {
      setZapLoading(true);

      const res = await api.post(
        "/integrations/zapier/create",
        { workspaceId }
      );

      const data = res.data;

      setIntegration({
        webhookUrl:
          data.webhookUrl ||
          `${process.env.REACT_APP_API_BASE_URL}/api/integrations/zapier/import`,
        token: data.token,
      });

      setZapierExpanded(true);
    } catch (err) {
      console.error("Failed to create integration:", err);
      setZapError("Failed to create integration");
    } finally {
      setZapLoading(false);
    }
  };

  const copyToClipboard = (value) => {
    if (!value) return;

    navigator.clipboard.writeText(value);
  };

  const maskedToken = (token) => {
    if (!token) return "";

    return token.slice(0, 6) + "••••••••••••••••";
  };

  // ── Database logic ──────────────────────────────────────
  const handleDbChange = (e) => {
    const { name, value } = e.target;

    setDbForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ── URL logic ───────────────────────────────────────────
  const handleUrlInputChange = (e) => {
    const { name, value } = e.target;

    setUrlForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUrlCheckbox = (field) => {
    setUrlForm((prev) => {
      if (field === "singlePage") {
        return {
          ...prev,
          singlePage: !prev.singlePage,
          allSubpages: false,
        };
      }

      if (field === "allSubpages") {
        return {
          ...prev,
          allSubpages: !prev.allSubpages,
          singlePage: false,
        };
      }

      return prev;
    });
  };

  const resetUrlScrapingState = () => {
    setUrlForm({
      url: "",
      singlePage: true,
      allSubpages: false,
    });
  };

  // ── Manual Upload logic ─────────────────────────────────
  const handleDropzoneClick = () => {
    document.getElementById("manual-upload-input")?.click();
  };

  const handleManualFilesChange = (e) => {
    const files = Array.from(e.target.files || []);

    setManualFiles(files);

    console.log("Selected files:", files);
  };

  const handleUpload = async () => {
    if (manualFiles.length === 0) {
      toast.warning("Please select at least one file");
      return;
    }

    try {
      setUploading(true);

      for (const singleFile of manualFiles) {
        const formData = new FormData();

        formData.append("file", singleFile);
        formData.append(
          "workspaceId",
          "a2f1d7c2-9c3c-4d1a-9c6f-111111111111"
        );
        formData.append("sourceType", "kb");
        formData.append("sourceId", uuidv4());
        formData.append("department", manualDepartment);
        formData.append("verified", manualVerified);

        await api.post("/embeddings/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      toast.success(
        `${manualFiles.length} file(s) uploaded successfully`
      );

      setManualFiles([]);

      const input = document.getElementById("manual-upload-input");

      if (input) {
        input.value = "";
      }

      onClose();
    } catch (err) {
      console.error("Upload error:", err);

      toast.error(
        "Upload failed: " +
        (err.response?.data?.message || err.message)
      );
    } finally {
      setUploading(false);
    }
  };

  // ── Cards ───────────────────────────────────────────────
  const attachmentMethods = [
    {
      id: "manual_upload",
      title: "Manual Upload",
      icons: [
        <img
          key="folder"
          src="/images/man1.svg"
          alt="manual upload"
          className="method-icon-img"
        />,
      ],
    },

    {
      id: "google_drive",
      title: "Drive",
      icons: [
        <img
          key="drive"
          src="/images/drive1.svg"
          alt="drive"
          className="method-icon-img"
        />,
      ],
    },

    {
      id: "url_scraping",
      title: "URL Scraping",
      icons: [
        <img
          key="url"
          src="/images/url1.svg"
          alt="url"
          className="method-icon-img"
        />,
      ],
    },

    {
      id: "zapier",
      title: "Zapier",
      icons: [
        <img
          key="zap"
          src="/images/zap1.svg"
          alt="zapier"
          className="method-icon-img"
        />,
      ],
    },
  ];

  return (
    <div className="amp-overlay">
      <div className="amp-modal">

        {/* HEADER */}

        <div className="amp-modal-header">
          <div>
            <h2 className="amp-title">
              Add Source
            </h2>

            <p className="amp-subtitle">
              Choose how you want to import data
            </p>
          </div>

          <button
            className="amp-close-btn"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="amp-divider" />

        <div className="amp-scroll-body">

          {/* TOP CARDS */}

          <div className="amp-cards-row">
            {attachmentMethods.map((method) => (
              <div
                key={method.id}
                className={`amp-card ${
                  selectedMethod === method.id
                    ? "amp-card-active"
                    : ""
                }`}
                onClick={() => setSelectedMethod(method.id)}
              >
                <div className="amp-icon-area">
                  {method.icons.map((icon, i) => (
                    <span
                      key={i}
                      className="amp-icon-slot"
                    >
                      {icon}
                    </span>
                  ))}
                </div>

                <h6 className="amp-card-title">
                  {method.title}
                </h6>

                <p className="amp-card-mini">
                  {method.id === "manual_upload" &&
                    "PDF, DOCX, TXT"}

                  {method.id === "google_drive" &&
                    "Google Drive OAuth"}

                  {method.id === "url_scraping" &&
                    "Public webpages"}

                  {method.id === "zapier" &&
                    "CRM automation"}
                </p>
              </div>
            ))}
          </div>

          {/* CONTENT */}

          <div className="amp-bottom">

            {/* MANUAL UPLOAD */}

            {selectedMethod === "manual_upload" && (
              <div className="amp-panel">

                <input
                  id="manual-upload-input"
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt"
                  className="amp-hidden-file-input"
                  onChange={handleManualFilesChange}
                />

                {/* TOP ROW */}

                <div className="amp-manual-footer">

                  <div className="amp-manual-field amp-manual-department">

                    <label className="amp-manual-label">
                      Department
                    </label>

                    <div className="amp-custom-select">

                      <button
                        type="button"
                        className="amp-custom-select-trigger"
                        onClick={() =>
                          setManualDeptOpen((prev) => !prev)
                        }
                      >
                        <span>
                          {manualDepartment}
                        </span>

                        <span
                          className={`amp-custom-arrow ${
                            manualDeptOpen ? "open" : ""
                          }`}
                        >
                          ▾
                        </span>
                      </button>

                      {manualDeptOpen && (
                        <div className="amp-custom-select-menu">

                          {[
                            "HR",
                            "Finance",
                            "Engineering",
                            "Marketing",
                          ].map((item) => (
                            <button
                              key={item}
                              type="button"
                              className={`amp-custom-option ${
                                manualDepartment === item
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => {
                                setManualDepartment(item);
                                setManualDeptOpen(false);
                              }}
                            >
                              {item}
                            </button>
                          ))}

                        </div>
                      )}
                    </div>
                  </div>

                  <div className="amp-manual-field amp-manual-verified">

                    <label className="amp-manual-label">
                      Verified
                    </label>

                    <label className="amp-switch">
                      <input
                        type="checkbox"
                        checked={manualVerified}
                        onChange={() =>
                          setManualVerified((prev) => !prev)
                        }
                      />

                      <span className="amp-slider"></span>
                    </label>
                  </div>
                </div>

                {/* DROPZONE */}

                <div
                  className="amp-dropzone"
                  onClick={handleDropzoneClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" ||
                      e.key === " "
                    ) {
                      e.preventDefault();
                      handleDropzoneClick();
                    }
                  }}
                >
                  <img
                    src="/images/man1.svg"
                    alt="upload"
                    className="amp-dropzone-icon"
                  />

                  <p className="amp-dropzone-title">
                    Drop files here or click to upload
                  </p>

                  <p className="amp-dropzone-sub">
                    Supports PDF, DOCX, TXT
                  </p>
                </div>

                {manualFiles.length > 0 && (
                  <p className="amp-manual-file-count">
                    {manualFiles.length} item
                    {manualFiles.length > 1 ? "s" : ""}
                    {" "}selected
                  </p>
                )}

                {/* BUTTONS */}

                <div className="amp-manual-actions">

                  <button
                    className="amp-manual-cancel"
                    onClick={onClose}
                  >
                    Cancel
                  </button>

                  <button
                    className="amp-btn-add amp-manual-add"
                    onClick={handleUpload}
                    disabled={
                      uploading ||
                      manualFiles.length === 0
                    }
                  >
                    {uploading
                      ? "Uploading..."
                      : "Save"}
                  </button>
                </div>
              </div>
            )}

            {/* GOOGLE DRIVE */}

            {selectedMethod === "google_drive" && (
              <div className="amp-panel">

                {!showDriveInside ? (
                  <div className="amp-drive-list">

                    <div className="amp-drive-row">

                      <div className="amp-drive-left">

                        <img
                          src="/images/Gdrive.svg"
                          alt="Google Drive"
                          className="amp-drive-logo"
                        />

                        <div className="amp-drive-info">
                          <span className="amp-drive-name">
                            Google Drive
                          </span>

                          <span className="amp-drive-status">
                            ⚠ Not logged in
                          </span>
                        </div>
                      </div>

                      <button
                        className="amp-btn-add amp-drive-login"
                        onClick={() =>
                          setShowDriveInside(true)
                        }
                      >
                        Login
                      </button>
                    </div>

                    <div className="amp-drive-row">

                      <div className="amp-drive-left">

                        <img
                          src="/images/Onecloud.svg"
                          alt="OneDrive"
                          className="amp-drive-logo"
                        />

                        <div className="amp-drive-info">
                          <span className="amp-drive-name">
                            OneDrive
                          </span>

                          <span className="amp-drive-status">
                            ⚠ Not logged in
                          </span>
                        </div>
                      </div>

                      <button className="amp-btn-add amp-drive-login">
                        Login
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      overflow: "visible",
                    }}
                  >
                    <GoogleDrivePage embedded={true} />
                  </div>
                )}
              </div>
            )}

            {/* ZAPIER */}

            {selectedMethod === "zapier" && (
              <div className="amp-panel">

                <div className="amp-zap-row">

                  <div className="amp-drive-left">

                    <span className="amp-drive-name">
                      Zapier
                    </span>

                    {integration ? (
                      <span className="amp-status-connected">
                        ✔ Connected
                      </span>
                    ) : (
                      <span className="amp-drive-status">
                        ⚠ Not connected
                      </span>
                    )}
                  </div>

{!zapierExpanded && (
  <button
    className="amp-btn-add amp-zap-connect-btn"
    onClick={handleZapierEnable}
    disabled={zapLoading}
  >
    {zapLoading
      ? "Creating..."
      : "Connect"}
  </button>
)}
                </div>

                {zapierExpanded && (
                  <div className="amp-zap-details">

                    {/* LEFT */}

                    <div className="amp-zap-left">

                      <div className="amp-zap-field">

                        <label className="amp-field-label">
                          Webhook URL
                        </label>

                        <div className="amp-input-row">

                          <input
                            type="text"
                            className="amp-input"
                            value={integration?.webhookUrl || ""}
                            readOnly
                            placeholder="Paste URL"
                          />

                          <button
                            className="amp-btn-outline"
                            onClick={() =>
                              copyToClipboard(
                                integration?.webhookUrl || ""
                              )
                            }
                          >
                            Copy
                          </button>
                        </div>
                      </div>

                      <div className="amp-zap-field">

                        <label className="amp-field-label">
                          API Key
                        </label>

                        <div className="amp-input-row">

                          <input
                            type="text"
                            className="amp-input"
                            value={
                              showToken
                                ? integration?.token || ""
                                : maskedToken(
                                    integration?.token || ""
                                  )
                            }
                            readOnly
                            placeholder="Enter API Key"
                          />

                          <button
                            className="amp-btn-outline"
                            onClick={() =>
                              setShowToken((prev) => !prev)
                            }
                          >
                            {showToken
                              ? "Hide"
                              : "View"}
                          </button>

                          <button
                            className="amp-btn-outline"
                            onClick={() =>
                              copyToClipboard(
                                integration?.token || ""
                              )
                            }
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT */}

                    <div className="amp-zap-right">

                      <div className="amp-zap-instructions">

                        <p className="amp-zap-instructions-title">
                          Setup in Zapier
                        </p>

                        <ul className="amp-zap-list">
                          <li>Create a new Zap.</li>

                          <li>
                            Select your CRM
                            (Salesforce, HubSpot, etc.)
                            as Trigger.
                          </li>

                          <li>
                            Select
                            "Webhooks by Zapier" → POST.
                          </li>

                          <li>
                            Paste the Webhook URL above.
                          </li>

                          <li>
                            Add Header:
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="amp-zap-actions">

  <button
    className="amp-zap-cancel"
    onClick={onClose}
  >
    Cancel
  </button>

  <button
    className="amp-btn-add amp-zap-save"
    onClick={onClose}
  >
    Save
  </button>

</div>
                  </div>
                )}

                {zapError && (
                  <p className="amp-error-text">
                    {zapError}
                  </p>
                )}
              </div>
            )}

            {/* DATABASE */}

            {selectedMethod === "database" && (
              <div className="amp-panel">

                <div className="amp-db-grid amp-db-grid-top">

                  <div className="amp-db-field">

                    <label className="amp-db-label">
                      Type
                    </label>

                    <div className="amp-db-select-wrap">

                      <select
                        name="type"
                        className="amp-db-select"
                        value={dbForm.type}
                        onChange={handleDbChange}
                      >
                        <option>PostgreSQL</option>
                        <option>MySQL</option>
                        <option>MariaDB</option>
                        <option>SQL Server</option>
                      </select>
                    </div>
                  </div>

                  <div className="amp-db-field">

                    <label className="amp-db-label">
                      Host
                    </label>

                    <input
                      type="text"
                      name="host"
                      className="amp-db-input"
                      value={dbForm.host}
                      onChange={handleDbChange}
                    />
                  </div>

                  <div className="amp-db-field amp-db-port-field">

                    <label className="amp-db-label">
                      Port
                    </label>

                    <input
                      type="text"
                      name="port"
                      className="amp-db-input"
                      value={dbForm.port}
                      onChange={handleDbChange}
                    />
                  </div>
                </div>

                <div className="amp-db-grid amp-db-grid-bottom">

                  <div className="amp-db-field">

                    <label className="amp-db-label">
                      PostgreSQL username
                    </label>

                    <input
                      type="text"
                      name="username"
                      className="amp-db-input"
                      value={dbForm.username}
                      onChange={handleDbChange}
                    />
                  </div>

                  <div className="amp-db-field">

                    <label className="amp-db-label">
                      Password
                    </label>

                    <input
                      type="password"
                      name="password"
                      className="amp-db-input"
                      value={dbForm.password}
                      onChange={handleDbChange}
                    />
                  </div>

                  <div className="amp-db-field amp-db-empty" />
                </div>

                <div className="amp-db-actions">

                  <button
                    className="amp-db-cancel"
                    onClick={() =>
                      setSelectedMethod(null)
                    }
                  >
                    Cancel
                  </button>

                  <button
                    className="amp-btn-add"
                    onClick={() =>
                      navigate("/database-connect")
                    }
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {/* URL SCRAPING */}

            {selectedMethod === "url_scraping" && (
              <div className="amp-panel">

                {!showUrlInside ? (
                  <div className="amp-url-section">

                    <div className="amp-url-field-wrap">

                      <label className="amp-url-label">
                        URL
                      </label>

                      <input
                        type="text"
                        name="url"
                        className="amp-url-input"
                        placeholder="Paste URL"
                        value={urlForm.url}
                        onChange={handleUrlInputChange}
                      />
                    </div>

                    <div className="amp-url-options">

                      <label className="amp-url-check-row">

                        <input
                          type="checkbox"
                          checked={urlForm.singlePage}
                          onChange={() =>
                            handleUrlCheckbox("singlePage")
                          }
                        />

                        <span>
                          Single Page
                        </span>
                      </label>

                      <label className="amp-url-check-row">

                        <input
                          type="checkbox"
                          checked={urlForm.allSubpages}
                          onChange={() =>
                            handleUrlCheckbox("allSubpages")
                          }
                        />

                        <span>
                          All Subpages(Sitemap)
                        </span>
                      </label>
                    </div>

                    {!urlForm.allSubpages && (
                      <div className="amp-url-actions amp-url-actions-single">

                        <button
                          className="amp-url-cancel-btn"
                          onClick={resetUrlScrapingState}
                        >
                          Cancel
                        </button>

                        <button
                          className="amp-btn-add"
                          onClick={() =>
                            setShowUrlInside(true)
                          }
                        >
                          Save
                        </button>
                      </div>
                    )}

                    {urlForm.allSubpages && (
                      <div className="amp-url-found-row">

                        <p className="amp-url-found-text">
                          12 pages found.
                          Proceed to scrape all pages?
                        </p>

                        <div className="amp-url-found-actions">

                          <button
                            className="amp-url-cancel-btn"
                            onClick={resetUrlScrapingState}
                          >
                            Cancel
                          </button>

                          <button
                            className="amp-btn-add amp-url-proceed-btn"
                            onClick={() =>
                              setShowUrlInside(true)
                            }
                          >
                            Proceed
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      overflow: "visible",
                    }}
                  >
                    <UrlImportPage embedded={true} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentMethodsPage;